import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import {
    AttachDealersToUserGroupDocument,
    type AttachDealersToUserGroupMutation,
    type AttachDealersToUserGroupMutationVariables,
} from '../../../api/mutations/attachDealersToUserGroup';
import { useListDealersQuery } from '../../../api/queries/listDealers';
import useHandleError from '../../../utilities/useHandleError';
import SelectDealersWithSearchField from './SelectDealersWithSearchField';

type FormValues = { dealers: string[] };

const initialValues: FormValues = { dealers: [] };

export type UserGroupAddDealerFormProps = {
    userGroup: UserGroupSpecsFragment;
    onComplete: () => void;
};

const UserGroupAddDealerForm = ({ userGroup, onComplete }: UserGroupAddDealerFormProps) => {
    const { t } = useTranslation(['userGroupDetails']);
    const apolloClient = useApolloClient();
    const userGroupId = userGroup.id;

    const { data, loading } = useListDealersQuery({
        fetchPolicy: 'cache-and-network',
    });

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('userGroupDetails:dealerDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<AttachDealersToUserGroupMutation, AttachDealersToUserGroupMutationVariables>({
                    mutation: AttachDealersToUserGroupDocument,
                    variables: { userGroupId, dealerIds: values.dealers },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('userGroupDetails:dealerDrawer.messages.success'),
                key: 'primary',
            });

            // then close the drawer
            onComplete();
        },
        [apolloClient, t, onComplete, userGroupId]
    );

    const dataSource = useMemo(() => data?.page.items || [], [data]);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id="addDealers" layout="vertical" name="addDealers" onSubmitCapture={handleSubmit}>
                    <SelectDealersWithSearchField
                        dealers={dataSource}
                        loading={loading}
                        mode="multiple"
                        name="dealers"
                        style={{ width: '100%' }}
                        userGroup={userGroup}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UserGroupAddDealerForm;
